import { trackConsumerEvent } from './consumerEvents';

export type StorefrontSurface = 'showcase' | 'collection' | 'categories' | 'category' | 'brand' | 'announcement';
export type StorefrontEvent = 'storefront_impression' | 'storefront_clicked' |
  'welcome_video_opened' | 'welcome_video_play_requested' | 'welcome_video_started' |
  'welcome_video_progress' | 'welcome_video_ended' | 'welcome_video_dismissed' |
  'welcome_video_suppressed' | 'welcome_video_failed' | 'welcome_video_replayed' | 'welcome_video_watch_summary';

// Clarity gets named actions, not identities, media URLs or customer properties.
// Motito's existing ingest endpoint resolves the authenticated actor/session.
export function trackStorefrontEvent(eventType: StorefrontEvent, properties: Record<string, string | number | boolean>): void {
  try { trackConsumerEvent({ eventType, properties }); } catch { /* Shopping must continue. */ }
  try {
    const clarityEvent = eventType.startsWith('storefront_')
      ? `${eventType}_${properties.surface}${properties.content_id === 'iphones' ? '_iphones' : ''}` : eventType;
    window.clarity?.('event', clarityEvent);
  } catch { /* Clarity is optional. */ }
}

export function trackStorefrontClick(surface: StorefrontSurface, contentId: string, destination?: string): void {
  let destinationPath: string | undefined;
  try {
    if (destination) {
      const url = new URL(destination, window.location.origin);
      const publicPath = /^\/(?:category(?:\/[^/]+)?|collections\/[^/]+|brands\/[^/]+|products\/[a-f0-9-]{36}|pay4me)\/?$/i;
      if (url.origin === window.location.origin && publicPath.test(url.pathname)) destinationPath = url.pathname;
    }
  } catch { /* No raw URLs in telemetry. */ }
  trackStorefrontEvent('storefront_clicked', {
    surface, content_id: contentId,
    ...(destinationPath ? { destination_path: destinationPath } : {}),
  });
}

export type ProductCardAnalytics = { collection_key: string; position: number };

/** Public catalogue identity and rendered rank; no customer or destination data. */
export function trackProductCardEvent(
  eventType: 'storefront_impression' | 'storefront_clicked',
  productId: string,
  context: ProductCardAnalytics,
): void {
  const collectionKey = context.collection_key.trim();
  // Within the ASCII slug alphabet, mirror the server's existing phone scrubber.
  const digits = collectionKey.replace(/-/g, '');
  const phoneLike = /^[0-9][0-9-]{8,}$/.test(collectionKey)
    && digits.length >= 8 && digits.length <= 15
    && (collectionKey.includes('-') || /^0[0-9]{9}$/.test(collectionKey) || /^233[0-9]{9}$/.test(collectionKey));
  // The 00 dialing prefix is separate from the 8-15 international-number digits.
  const internationalPhoneLike = /^00[1-9][0-9]{7,14}$/.test(digits);
  if (!/^[a-f0-9]{8}(?:-[a-f0-9]{4}){3}-[a-f0-9]{12}$/i.test(productId)
    || !/^[a-zA-Z0-9_-]{1,120}$/.test(collectionKey) || phoneLike || internationalPhoneLike
    || !Number.isInteger(context.position) || context.position < 1 || context.position > 100) return;
  trackStorefrontEvent(eventType, {
    surface: 'product_card', product_id: productId.toLowerCase(),
    collection_key: collectionKey.toLowerCase(), position: context.position,
  });
}
